/**
 * business_search_controller.js — business autocomplete on the join page (Stimulus).
 *
 * Ports static/js/business_join.js to an element-scoped controller, one instance
 * per join form (data-controller="business-search" on #joinForm). Provides
 * progressive-enhancement search: typing a business name queries
 * /auth/search-businesses (debounced), renders matches that accept join requests,
 * and clicking one fills the required 6-char Business ID. The form still works
 * with JS disabled — the ID can always be typed manually.
 *
 * Why a controller: the old IIFE bound listeners at script-load time from the
 * page <head> with no defer, so on a hard refresh it ran before <body> existed,
 * found no elements, and silently bailed — search did nothing. Stimulus connects
 * this only once its element is in the DOM (and reconnects across Turbo nav),
 * which structurally removes that load-order race.
 *
 * Lifecycle: connect() resolves child elements and seeds debounce state; events
 * are wired declaratively via data-action (search input, result clicks, submit),
 * so Stimulus owns those listeners. disconnect() only clears the pending debounce
 * timer — the one piece of state Stimulus does not manage.
 */

import { Controller } from "@hotwired/stimulus";

    var DEBOUNCE_MS = 300;
    var MIN_QUERY_LENGTH = 2;

    function escapeHtml(text) {
        var div = document.createElement('div');
        div.textContent = text || '';
        return div.innerHTML;
    }

export default class extends Controller {
        connect() {
            this.searchInput = this.element.querySelector('#business_search');
            this.resultsDiv = this.element.querySelector('#search_results');
            this.businessIdInput = this.element.querySelector('#business_id');
            this.submitBtn = this.element.querySelector('#submit_btn');
            this._searchTimeout = null;
        }

        disconnect() {
            clearTimeout(this._searchTimeout);
            this._searchTimeout = null;
        }

        // input->business-search#search
        search() {
            clearTimeout(this._searchTimeout);
            var query = this.searchInput.value.trim();

            if (query.length < MIN_QUERY_LENGTH) {
                this.resultsDiv.style.display = 'none';
                return;
            }

            this._searchTimeout = setTimeout(() => this._fetchBusinesses(query), DEBOUNCE_MS);
        }

        _fetchBusinesses(query) {
            this.resultsDiv.innerHTML =
                '<div class="text-center p-3"><span class="spinner-border spinner-border-sm"></span> Searching...</div>';
            this.resultsDiv.style.display = 'block';

            window.api('/auth/search-businesses?query=' + encodeURIComponent(query), { notifyError: false })
                .then((data) => {
                    if (data.success && data.data.length > 0) {
                        this._renderResults(data.data);
                    } else {
                        this.resultsDiv.innerHTML = '<div class="p-3 text-muted">No businesses found</div>';
                    }
                })
                .catch((error) => {
                    console.error('[BusinessSearch] Search error:', error);
                    this.resultsDiv.innerHTML =
                        '<div class="p-3 text-danger">Search failed. You can enter the business ID manually below.</div>';
                });
        }

        // Result items carry their own data-action so Stimulus binds the click
        // even though they are injected after connect().
        _renderResults(businesses) {
            var html = businesses
                .filter((b) => b.allow_join_requests)
                .map((biz) => `
                    <div class="list-group-item list-group-item-action" style="cursor: pointer;"
                         data-action="click->business-search#select"
                         data-business-id="${escapeHtml(biz.business_id)}"
                         data-business-name="${escapeHtml(biz.name)}">
                        <div class="d-flex w-100 justify-content-between">
                            <h6 class="mb-1">${escapeHtml(biz.name)}</h6>
                            <small class="text-muted">${escapeHtml(biz.business_id)}</small>
                        </div>
                        ${biz.city || biz.state ? `<p class="mb-1 small text-muted">${escapeHtml(biz.city || '')}, ${escapeHtml(biz.state || '')}</p>` : ''}
                        ${biz.description ? `<p class="mb-0 small">${escapeHtml(biz.description)}</p>` : ''}
                    </div>
                `).join('');

            if (html) {
                this.resultsDiv.innerHTML = '<div class="list-group">' + html + '</div>';
            } else {
                this.resultsDiv.innerHTML =
                    '<div class="p-3 text-muted">No businesses accepting join requests found</div>';
            }
        }

        // click->business-search#select
        select(event) {
            var item = event.currentTarget;
            this.businessIdInput.value = item.dataset.businessId;
            this.searchInput.value = item.dataset.businessName;
            this.resultsDiv.style.display = 'none';
        }

        // submit->business-search#validate
        validate(event) {
            var businessId = this.businessIdInput.value.trim();

            if (!businessId) {
                event.preventDefault();
                alert('Please search for a business and select it, or enter a business ID manually.');
                this.businessIdInput.focus();
                return;
            }

            this.submitBtn.disabled = true;
            this.submitBtn.innerHTML =
                '<span class="spinner-border spinner-border-sm me-2"></span>Submitting...';
        }
}
